import React from 'react'
import { Component } from 'reflux'
import {
  Paper,
  Box,
  Typography,
  TextField,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  IconButton,
  Collapse,
  Chip,
  TablePagination,
  Avatar,
  Tooltip,
  Divider,
  Dialog,
  DialogContent,
  DialogTitle
} from '@material-ui/core'
import ExpandMoreIcon from '@material-ui/icons/ExpandMore'
import ExpandLessIcon from '@material-ui/icons/ExpandLess'
import CloseIcon from '@material-ui/icons/Close'
import AddIcon from '@material-ui/icons/Add'
import { ProductSyncActions, ProductSyncStore } from '../../stores/productSyncStore'
import { Button, Checkbox, FormControlLabel, Grid } from '@material-ui/core'
import { mapSupplierProductToInternal } from './nwgMapper'
import { STATUS } from '../../stores/productSyncStore'

export default class ProductSyncDetails extends Component {
  constructor(props) {
    super(props)
    this.store = ProductSyncStore
    this.storeKeys = ['products', 'pageInfo', 'totalCount', 'loading', 'status', 'redirectTo']
    this.state = {
      ...this.state,
      search: '',
      expanded: {},
      imageViewerOpen: false,
      imageViewerSrc: null,
      imageViewerAlt: '',
      exportDialogOpen: false,
      exportProduct: null,
      exportSelection: {},
      selectedImages: new Set(),
      includeFabricInText: false
    }
    this.searchTimer = null
  }

  componentDidMount() {
    const supplierID = this.props.match?.params?.SupplierID
    ProductSyncActions.getProducts(supplierID, { page: 1, pageSize: 20, q: this.state.search })
  }

  toggleExpand = productNumber => {
    this.setState(prev => ({
      expanded: { ...prev.expanded, [productNumber]: !prev.expanded[productNumber] }
    }))
  }

  handleSearch = e => {
    const search = e.target.value
    this.setState({ search })
    clearTimeout(this.searchTimer)
    this.searchTimer = setTimeout(() => {
      const supplierID = this.props.match?.params?.SupplierID ?? 40
      ProductSyncActions.getProducts(supplierID, { page: 1, pageSize: this.state?.pageInfo?.pageSize || 20, q: search })
    }, 350)
  }

  handleChangePage = (_, newPage) => {
    const supplierID = this.props.match?.params?.SupplierID ?? 40
    const { pageInfo } = this.state
    ProductSyncActions.getProducts(supplierID, {
      page: newPage + 1,
      pageSize: pageInfo?.pageSize || 20,
      q: this.state.search || ''
    })
  }

  handleChangeRowsPerPage = e => {
    const pageSize = parseInt(e.target.value, 10)
    const supplierID = this.props.match?.params?.SupplierID ?? 40
    ProductSyncActions.getProducts(supplierID, {
      page: 1,
      pageSize,
      q: this.state.search || ''
    })
  }

  openImage = (src, alt) => {
    if (!src) return
    this.setState({ imageViewerOpen: true, imageViewerSrc: src, imageViewerAlt: alt || '' })
  }

  closeImage = () => {
    this.setState({ imageViewerOpen: false, imageViewerSrc: null, imageViewerAlt: '' })
  }

  openExportDialog = product => {
    const selection = {}
    ;(product?.variations || []).forEach(v => {
      selection[v.itemNumber] = { selected: false, skus: new Set() }
    })
    this.setState({
      exportDialogOpen: true,
      exportProduct: product,
      exportSelection: selection,
      selectedImages: new Set()
    })
  }

  closeExportDialog = () => {
    this.setState({
      exportDialogOpen: false,
      exportProduct: null,
      exportSelection: {},
      selectedImages: new Set()
    })
  }

  toggleVariation = (variationItemNumber, checked) => {
    this.setState(prev => {
      const sel = { ...prev.exportSelection }
      const vEntry = sel[variationItemNumber] || { selected: false, skus: new Set() }
      vEntry.selected = checked
      if (checked) {
        const variation = (prev.exportProduct?.variations || []).find(v => v.itemNumber === variationItemNumber)
        vEntry.skus = new Set((variation?.skus || []).map(s => s.sku))
      } else {
        vEntry.skus = new Set()
      }
      sel[variationItemNumber] = vEntry
      return { exportSelection: sel }
    })
  }

  toggleSku = (variationItemNumber, skuId, checked) => {
    this.setState(prev => {
      const sel = { ...prev.exportSelection }
      const vEntry = sel[variationItemNumber] || { selected: false, skus: new Set() }
      const skus = new Set(vEntry.skus)
      if (checked) {
        skus.add(skuId)
      } else {
        skus.delete(skuId)
      }
      vEntry.skus = skus
      vEntry.selected = skus.size > 0
      sel[variationItemNumber] = vEntry
      return { exportSelection: sel }
    })
  }

  handleExportConfirm = () => {
    const product = this.state.exportProduct
    if (!product) return
    const selection = this.state.exportSelection || {}
    const filteredVariations = (product.variations || [])
      .filter(v => {
        const sel = selection[v.itemNumber]
        return sel && sel.selected && sel.skus && sel.skus.size > 0
      })
      .map(v => {
        const sel = selection[v.itemNumber]
        const filteredSkus = (v.skus || []).filter(sku => sel.skus.has(sku.sku))
        return { ...v, skus: filteredSkus }
      })

    const filteredProduct = { ...product, variations: filteredVariations }
    const payload = mapSupplierProductToInternal(filteredProduct, {
      includeFabricInText: !!this.state.includeFabricInText,
      defaultProductPrice: '0',
      defaultRetailPrice: '0',
      defaultProductPriceAdjust: '0',
      defaultProfitPercentage: '0',
      defaultWeightInGram: 0,
      timeStart: new Date().toISOString(),
      supplierID: 40
    })

    const imgs = Array.from(this.state.selectedImages || [])
    if (imgs.length > 0) {
      payload.selectedImages = imgs
    }

    ProductSyncActions.exportSelection ? ProductSyncActions.exportSelection(payload) : console.error('Internal payload:', payload)

    this.closeExportDialog()
  }

  // Utility: first up to 4 images of a variation
  getVariationImages = variation => {
    const pics = Array.isArray(variation?.pictures) ? variation.pictures : []
    return pics
      .slice(0, 6)
      .map(p => p.imageUrl)
      .filter(Boolean)
  }

  getThumbnail = product => {
    const pic = product.variations?.[0]?.pictures?.[0]?.imageUrl || null
    return pic || 'https://via.placeholder.com/64?text=IMG'
  }

  getVariationImage = (product, variation) => {
    const url =
      variation?.pictures?.[0]?.imageUrl || variation?.imageUrl || product?.picture?.url || product?.variations?.[0]?.pictures?.[0]?.imageUrl || null
    return url || 'https://via.placeholder.com/160?text=IMG'
  }

  renderVariations = product => {
    const vars = Array.isArray(product.variations) ? product.variations : []
    if (vars.length === 0) {
      return (
        <Box px={2} py={1}>
          <Typography variant='body2' color='textSecondary'>
            Ingen variationer
          </Typography>
        </Box>
      )
    }
    return (
      <Box px={2} py={1}>
        {vars.map(v => {
          const skus = Array.isArray(v.skus) ? v.skus : []
          const regionalTotal = skus.reduce((acc, s) => acc + (Number.isFinite(s.availabilityRegional) ? s.availabilityRegional : 0), 0)
          const globalTotal = skus.reduce((acc, s) => acc + (Number.isFinite(s.availabilityGlobal) ? s.availabilityGlobal : 0), 0)
          const img = this.getVariationImage(product, v)

          return (
            <Box key={v.itemNumber} mb={2}>
              {/* Variation header */}
              <Box
                display='flex'
                alignItems='flex-start'
                style={{
                  gap: 16,
                  backgroundColor: '#fafafa',
                  border: '1px solid #eee',
                  borderRadius: 8,
                  padding: 12
                }}
              >
                <Box
                  component='img'
                  src={img}
                  alt={v.itemColorName || v.itemNumber}
                  onClick={() => this.openImage(img, `${product.productName} – ${v.itemColorName || v.itemColorCode || v.itemNumber}`)}
                  style={{
                    width: 120,
                    height: 120,
                    objectFit: 'cover',
                    borderRadius: 8,
                    background: '#fff',
                    border: '1px solid #eaeaea',
                    cursor: 'zoom-in'
                  }}
                />
                <Box display='flex' flexDirection='column'>
                  <Box display='flex' alignItems='center' style={{ gap: 8 }}>
                    <Chip size='small' label={v.itemColorName || v.itemColorCode || 'Variant'} />
                    <Typography variant='body2' color='textSecondary'>
                      {v.itemColorCode || ''}
                    </Typography>
                  </Box>
                  <Typography variant='subtitle2' style={{ marginTop: 4, fontWeight: 600 }}>
                    {product.productName}
                  </Typography>
                  <Typography variant='body2' color='textSecondary'>
                    Variant nr.: {v.itemNumber}
                  </Typography>
                  <Box mt={1} display='flex' style={{ gap: 16 }}>
                    <Typography variant='body2'>SKU’er: {skus.length}</Typography>
                    <Typography variant='body2'>Regional: {regionalTotal.toLocaleString('da-DK')}</Typography>
                    <Typography variant='body2'>Global: {globalTotal.toLocaleString('da-DK')}</Typography>
                  </Box>
                </Box>
              </Box>

              {/* SKUs table */}
              <Box mt={1}>
                <Table size='small'>
                  <TableHead>
                    <TableRow>
                      <TableCell style={{ width: 150 }}>Webtext</TableCell>
                      <TableCell style={{ width: 50 }}>Size</TableCell>
                      <TableCell>SKU</TableCell>
                      <TableCell>EAN</TableCell>
                      <TableCell align='right'>Regional</TableCell>
                      <TableCell align='right'>Global</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {skus.map(s => (
                      <TableRow key={s.sku}>
                        <TableCell>{s.webtext || '-'}</TableCell>
                        <TableCell>{s.size || '-'}</TableCell>
                        <TableCell>{s.sku}</TableCell>
                        <TableCell>{s.eanCode || '-'}</TableCell>
                        <TableCell align='right'>{s.availabilityRegional === null ? '-' : s.availabilityRegional}</TableCell>
                        <TableCell align='right'>{s.availabilityGlobal === null ? '-' : s.availabilityGlobal}</TableCell>
                      </TableRow>
                    ))}
                    {skus.length === 0 && (
                      <TableRow>
                        <TableCell colSpan={6}>
                          <Typography variant='caption' color='textSecondary'>
                            Ingen SKU’er
                          </Typography>
                        </TableCell>
                      </TableRow>
                    )}
                  </TableBody>
                </Table>
              </Box>

              <Divider style={{ marginTop: 12 }} />
            </Box>
          )
        })}
      </Box>
    )
  }

  render() {
    const { products, pageInfo, totalCount } = this.state || {}
    const list = Array.isArray(products) ? products : []
    const page = (pageInfo?.page || 1) - 1
    const pageSize = pageInfo?.pageSize || 20

    const exportProduct = this.state.exportProduct
    const exportVariations = exportProduct?.variations || []
    const selectedImages = this.state.selectedImages || new Set()

    return (
      <Paper style={{ padding: 16, border: '1px solid #e0e0e0', borderRadius: 8 }}>
        <Box mb={2} display='flex' alignItems='center' justifyContent='space-between'>
          <Typography variant='h6'>Produkter</Typography>
          <TextField
            label='Søg produkt, brand eller nummer'
            variant='outlined'
            size='small'
            value={this.state.search}
            onChange={this.handleSearch}
            style={{ width: 360 }}
          />
        </Box>

        <TableContainer>
          <Table size='small'>
            <TableHead>
              <TableRow>
                <TableCell style={{ width: 48 }} />
                <TableCell style={{ width: 72 }}>Billede</TableCell>
                <TableCell>Produkt</TableCell>
                <TableCell>Brand</TableCell>
                <TableCell>Nummer</TableCell>
                <TableCell align='right'>Pris</TableCell>
                <TableCell align='right'>Varianter</TableCell>
                <TableCell align='right' style={{ width: 64 }}>
                  Actions
                </TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {this.state.status === STATUS.IS_FETCHING ? (
                <TableRow>
                  <TableCell colSpan={100}>Indlæser…</TableCell>
                </TableRow>
              ) : list.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={100}>Ingen produkter.</TableCell>
                </TableRow>
              ) : (
                list.map(p => {
                  const isOpen = !!this.state.expanded[p.productNumber]
                  const thumb = this.getThumbnail(p)
                  const variantCount = Array.isArray(p.variations) ? p.variations.length : 0
                  return (
                    <React.Fragment key={p.productNumber || p.productName}>
                      <TableRow hover>
                        <TableCell>
                          <Tooltip title={isOpen ? 'Luk' : 'Udvid'}>
                            <IconButton size='small' onClick={() => this.toggleExpand(p.productNumber)}>
                              {isOpen ? <ExpandLessIcon /> : <ExpandMoreIcon />}
                            </IconButton>
                          </Tooltip>
                        </TableCell>
                        <TableCell>
                          <Avatar
                            variant='rounded'
                            src={thumb}
                            alt={p.productName}
                            onClick={() => this.openImage(thumb, p.productName)}
                            style={{ width: 48, height: 48, cursor: thumb ? 'zoom-in' : 'default' }}
                          />
                        </TableCell>
                        <TableCell>
                          <Box display='flex' flexDirection='column'>
                            <Typography variant='body2' style={{ fontWeight: 600 }}>
                              {p.productName}
                            </Typography>
                            <Typography variant='caption' color='textSecondary'>
                              {p.productBrand}
                            </Typography>
                          </Box>
                        </TableCell>
                        <TableCell>{p.productBrand || '-'}</TableCell>
                        <TableCell>{p.productNumber || '-'}</TableCell>
                        <TableCell align='right'>
                          {p.price === null ? '-' : Number(p.price).toLocaleString('da-DK', { minimumFractionDigits: 2 })}
                        </TableCell>
                        <TableCell align='right'>
                          <Chip size='small' label={`${variantCount} var.`} />
                        </TableCell>
                        <TableCell align='right'>
                          <Tooltip title='Vælg variation/SKU til eksport'>
                            <IconButton size='small' onClick={() => this.openExportDialog(p)}>
                              <AddIcon />
                            </IconButton>
                          </Tooltip>
                        </TableCell>
                      </TableRow>

                      <TableRow>
                        <TableCell colSpan={100} style={{ paddingBottom: 0, paddingTop: 0 }}>
                          <Collapse in={isOpen} timeout='auto' unmountOnExit>
                            {this.renderVariations(p)}
                          </Collapse>
                        </TableCell>
                      </TableRow>
                    </React.Fragment>
                  )
                })
              )}
            </TableBody>
          </Table>
        </TableContainer>

        <TablePagination
          component='div'
          count={totalCount || 0}
          page={page}
          onPageChange={this.handleChangePage}
          rowsPerPage={pageSize}
          onRowsPerPageChange={this.handleChangeRowsPerPage}
          rowsPerPageOptions={[10, 20, 50, 100]}
          labelRowsPerPage='Rækker pr. side'
        />

        {/* Image Lightbox */}
        <Dialog open={!!this.state.imageViewerOpen} onClose={this.closeImage} maxWidth='md' fullWidth>
          <DialogTitle>
            {this.state.imageViewerAlt || 'Billede'}
            <IconButton aria-label='close' onClick={this.closeImage} style={{ position: 'absolute', right: 8, top: 8 }}>
              <CloseIcon />
            </IconButton>
          </DialogTitle>
          <DialogContent dividers onClick={this.closeImage}>
            <Box display='flex' justifyContent='center'>
              <Box
                component='img'
                src={this.state.imageViewerSrc || ''}
                alt={this.state.imageViewerAlt}
                style={{ maxWidth: '100%', maxHeight: '80vh' }}
              />
            </Box>
          </DialogContent>
        </Dialog>

        {/* Export selection dialog */}
        <Dialog open={!!this.state.exportDialogOpen} onClose={this.closeExportDialog} maxWidth='md' fullWidth>
          <DialogTitle>
            Vælg variationer og SKU’er til eksport
            <IconButton aria-label='close' onClick={this.closeExportDialog} style={{ position: 'absolute', right: 8, top: 8 }}>
              <CloseIcon />
            </IconButton>
          </DialogTitle>
          <DialogContent dividers>
            {exportProduct ? (
              <Box display='flex' flexDirection='column' style={{ gap: 16 }}>
                <Box>
                  <Typography variant='subtitle2' style={{ fontWeight: 600 }}>
                    {exportProduct.productName}
                  </Typography>
                  <Typography variant='body2' color='textSecondary'>
                    {exportProduct.productBrand}
                  </Typography>
                  {exportProduct.productText && (
                    <Typography variant='body2' style={{ marginTop: 8 }}>
                      {exportProduct.productText}
                    </Typography>
                  )}
                </Box>

                {/* Export options */}
                <Box>
                  <FormControlLabel
                    control={
                      <Checkbox
                        color='primary'
                        checked={!!this.state.includeFabricInText}
                        onChange={e => this.setState({ includeFabricInText: e.target.checked })}
                      />
                    }
                    label='Inkludér materiale i beskrivelsestekst'
                  />
                </Box>

                {/* Variations list */}
                {exportVariations.map(v => {
                  const vSel = this.state.exportSelection[v.itemNumber] || { selected: false, skus: new Set() }
                  const images = this.getVariationImages(v)
                  const skus = Array.isArray(v.skus) ? v.skus : []
                  return (
                    <Box key={v.itemNumber} p={1} style={{ border: '1px solid #eee', borderRadius: 8, marginBottom: 12 }}>
                      <Box display='flex' alignItems='flex-start' style={{ gap: 12 }}>
                        {/* Images (first 4) */}
                        <Grid container spacing={1} style={{ maxWidth: 320 }}>
                          {images.length > 0 ? (
                            images.map((src, idx) => (
                              <Grid item xs={6} key={idx}>
                                <Box position='relative' style={{ borderRadius: 6, overflow: 'hidden', border: '1px solid #ddd' }}>
                                  <Box
                                    component='img'
                                    src={src}
                                    alt={`${v.itemColorName || v.itemColorCode} ${idx + 1}`}
                                    onClick={() => this.openImage(src, `${exportProduct.productName} – ${v.itemColorName || v.itemColorCode}`)}
                                    style={{ width: '100%', height: 100, objectFit: 'cover', cursor: 'zoom-in' }}
                                  />
                                  <Box position='absolute' top={4} right={4} style={{ background: 'rgba(255,255,255,0.9)', borderRadius: 4 }}>
                                    <Checkbox
                                      color='primary'
                                      checked={selectedImages.has(src)}
                                      onChange={e => {
                                        const checked = e.target.checked
                                        this.setState(prev => {
                                          const set = new Set(prev.selectedImages || [])
                                          if (checked) set.add(src)
                                          else set.delete(src)
                                          return { selectedImages: set }
                                        })
                                      }}
                                    />
                                  </Box>
                                </Box>
                              </Grid>
                            ))
                          ) : (
                            <Grid item xs={12}>
                              <Box style={{ width: '100%', height: 100, borderRadius: 6, border: '1px solid #ddd', background: '#fafafa' }} />
                            </Grid>
                          )}
                        </Grid>

                        {/* Variation info + checkbox */}
                        <Box flex={1}>
                          <Box display='flex' alignItems='center' justifyContent='space-between'>
                            <Box>
                              <Typography variant='subtitle2' style={{ fontWeight: 600 }}>
                                {v.itemColorName || v.itemColorCode || 'Variant'}
                              </Typography>
                              <Typography variant='body2' color='textSecondary'>
                                {v.itemNumber}
                              </Typography>
                            </Box>
                            <FormControlLabel
                              control={
                                <Checkbox
                                  color='primary'
                                  checked={!!vSel.selected}
                                  onChange={e => this.toggleVariation(v.itemNumber, e.target.checked)}
                                />
                              }
                              label='Vælg variation'
                            />
                          </Box>

                          {/* SKUs table with ONLY regional stock */}
                          <Box mt={2}>
                            <Table size='small'>
                              <TableHead>
                                <TableRow>
                                  <TableCell>SKU</TableCell>
                                  <TableCell>EAN</TableCell>
                                  <TableCell align='right'>Regional</TableCell>
                                  <TableCell>Size</TableCell>
                                  <TableCell>Webtext</TableCell>
                                  <TableCell align='right'>Valgt</TableCell>
                                </TableRow>
                              </TableHead>
                              <TableBody>
                                {skus.map(s => {
                                  const checked = vSel.skus.has(s.sku)
                                  return (
                                    <TableRow key={`stock-${s.sku}`}>
                                      <TableCell>{s.sku}</TableCell>
                                      <TableCell>{s.eanCode || '-'}</TableCell>
                                      <TableCell align='right'>{s.availabilityRegional === null ? '-' : s.availabilityRegional}</TableCell>
                                      <TableCell>{s.size || '-'}</TableCell>
                                      <TableCell>{s.webtext || '-'}</TableCell>
                                      <TableCell align='right'>
                                        <Checkbox
                                          color='primary'
                                          checked={checked}
                                          onChange={e => this.toggleSku(v.itemNumber, s.sku, e.target.checked)}
                                        />
                                      </TableCell>
                                    </TableRow>
                                  )
                                })}
                                {skus.length === 0 && (
                                  <TableRow>
                                    <TableCell colSpan={6}>
                                      <Typography variant='caption' color='textSecondary'>
                                        Ingen SKU’er
                                      </Typography>
                                    </TableCell>
                                  </TableRow>
                                )}
                              </TableBody>
                            </Table>
                          </Box>
                        </Box>
                      </Box>
                    </Box>
                  )
                })}

                <Box display='flex' justifyContent='flex-end' style={{ gap: 8 }}>
                  <Button onClick={this.closeExportDialog}>Annuller</Button>
                  <Button
                    color='primary'
                    variant='contained'
                    onClick={this.handleExportConfirm}
                    disabled={Object.values(this.state.exportSelection).every(v => !v.selected || v.skus.size === 0)}
                  >
                    Eksporter
                  </Button>
                </Box>
              </Box>
            ) : (
              <Typography variant='body2'>Ingen produkt valgt.</Typography>
            )}
          </DialogContent>
        </Dialog>
      </Paper>
    )
  }
}
